/** Navigation types and helpers shared by server and client components. */

/** Link target that opens the Discussions Filters panel ("More topics"). */
export const FILTERS_HASH = "#filters";

export interface EditorialNavLink {
	label: string;
	href: string;
	/** Paths that mark this link as the current section. */
	matches?: string[];
	/** Match the href exactly rather than as a prefix. */
	exact?: boolean;
	/**
	 * Set by the server when "current" depends on more than the path, e.g. a
	 * topic chosen with `?topic=`. Overrides path matching.
	 */
	current?: boolean;
	/** Account links shown as an icon in the corner (label stays for screen readers and the phone menu). */
	icon?: "messages" | "admin" | "notifications";
	/** The profile link: shown with the member's avatar. */
	avatarUrl?: string | null;
	/** Unread count shown on an icon link (messages, notifications). */
	badge?: number;
}

export interface EditorialPlaceNav {
	/** Scene (or other place) name shown under its primary section. */
	title: string;
	href: string;
	links: EditorialNavLink[];
	/** Section the place belongs to, e.g. "/scenes". */
	parentHref: string;
	/**
	 * List the links straight under the section, with no title row (e.g.
	 * Discussions topics), instead of title + nested subpages (a Scene).
	 */
	flat?: boolean;
}

/** Scene subpages, in nav order. */
export const SCENE_SECTIONS = [
	{ key: "feed", label: "Feed", path: "" },
	{ key: "stories", label: "Stories", path: "/stories" },
	{ key: "calendar", label: "Calendar", path: "/calendar" },
	{ key: "chat", label: "Chat", path: "/chat" },
	{ key: "drive", label: "Drive", path: "/drive" },
] as const;

export type SceneSectionKey = (typeof SCENE_SECTIONS)[number]["key"];

export function getScenePlaceNav(scene: {
	slug: string;
	title: string;
}): EditorialPlaceNav {
	const base = `/scenes/${scene.slug}`;
	return {
		title: scene.title,
		href: base,
		parentHref: "/scenes",
		links: SCENE_SECTIONS.map((section) => ({
			label: section.label,
			href: `${base}${section.path}`,
			exact: section.path === "",
		})),
	};
}

export function isNavLinkActive(
	link: EditorialNavLink,
	pathname: string,
): boolean {
	if (link.current !== undefined) return link.current;
	if (link.exact) return pathname === link.href;
	const candidates = link.matches?.length ? link.matches : [link.href];
	return candidates.some((candidate) => {
		if (candidate === "/") return pathname === "/";
		const base = candidate.replace(/\/$/, "");
		return pathname === base || pathname.startsWith(`${base}/`);
	});
}
